import { useMemo, useState } from "react";
import { PriceList } from "@forgepoint/shared";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Loader, Loader2, Upload } from "lucide-react";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { usePriceListsStore } from "./priceListsStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import PriceListEditorSheet from "./PriceListEditorSheet";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import { CSV_IMPORT_TYPE, Permission } from "@forgepoint/shared";
import { useHasPermission } from "@/store/authStore";

/** Records controller for managing customer-specific price lists. */
export default function PriceListsController() {
  const canEditPriceLists = useHasPermission(Permission.PRICELISTS_EDIT);

  const {
    priceLists,
    listLoading,
    listError,
    priceListLoadingStates,
    fetchPriceLists,
    createPriceList,
    archivePriceList,
    unarchivePriceList,
  } = usePriceListsStore();

  const [showArchived, setShowArchived] = useState(false);

  const { entitiesArray, handleSearch, setDisplayedEntityIds } =
    useEntitySearch<PriceList>({
      entities: priceLists,
      fetchEntities: (params) =>
        fetchPriceLists({ ...params, includeArchived: showArchived }),
    });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isImportSheetOpen, setIsImportSheetOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [selectedPriceList, setSelectedPriceList] = useState<PriceList | null>(
    null,
  );
  const [pendingArchiveId, setPendingArchiveId] = useState<string | null>(null);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const columns: DataTableColumn<PriceList>[] = useMemo(() => {
    const baseColumns: DataTableColumn<PriceList>[] = [
      {
        key: "name",
        header: "Name",
        type: "string",
      },
      {
        key: "description",
        header: "Description",
        type: "string",
      },
      {
        key: "lines",
        header: "Items",
        type: "number",
        width: 90,
        format: (_value, priceList) => priceList.lineCount ?? 0,
      },
      {
        key: "updatedAt",
        header: "Updated",
        type: "date",
        width: 120,
      },
    ];

    if (!canEditPriceLists) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        key: "actions",
        header: "",
        width: 60,
        format: (_value, priceList) => {
          const loadingState = priceListLoadingStates.get(priceList.id);
          const isLoadingArchive = loadingState?.isLoadingArchive;
          const isArchived = !!priceList.archivedAt;
          const isSynced = priceList.externalMappings.length > 0;

          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                if (isArchived) {
                  handleUnarchive(priceList.id);
                } else {
                  setPendingArchiveId(priceList.id);
                  setIsArchiveDialogOpen(true);
                }
              }}
              disabled={isLoadingArchive || isSynced}
            >
              {isLoadingArchive ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : isArchived ? (
                <ArchiveRestore className="h-4 w-4" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
            </Button>
          );
        },
      },
    ];
  }, [priceListLoadingStates, showArchived, canEditPriceLists]);

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    fetchPriceLists({ includeArchived: checked }).then((ids) => {
      setDisplayedEntityIds(ids);
    });
  };

  const handleArchiveConfirm = async () => {
    if (!pendingArchiveId) {
      return;
    }
    const success = await archivePriceList(pendingArchiveId);
    setIsArchiveDialogOpen(false);
    setPendingArchiveId(null);
    if (success) {
      toast.success("Price list archived");
      setDisplayedEntityIds((current) =>
        current.filter((id) => id !== pendingArchiveId),
      );
    } else {
      toast.error("Failed to archive price list");
    }
  };

  const handleUnarchive = async (id: string) => {
    const success = await unarchivePriceList(id);
    if (success) {
      toast.success("Price list unarchived");
    } else {
      toast.error("Failed to unarchive price list");
    }
  };

  const handleOpenCreate = () => {
    setCreateName("");
    setCreateDescription("");
    setIsCreateSheetOpen(true);
  };

  const handleEdit = (priceList: PriceList) => {
    setSelectedPriceList(priceList);
    setIsEditSheetOpen(true);
  };

  const handleArchived = (id: string) => {
    setIsEditSheetOpen(false);
    setDisplayedEntityIds((current) => current.filter((i) => i !== id));
  };

  const handleSaveCreate = async () => {
    if (!createName.trim()) {
      toast.error("Price list name is required");
      return;
    }

    setIsCreating(true);
    const { priceList, error: createError } = await createPriceList({
      name: createName.trim(),
      description: createDescription.trim() || null,
    });
    setIsCreating(false);

    if (priceList) {
      toast.success("Price list created");
      setDisplayedEntityIds((current) => [priceList.id, ...current]);
      setIsCreateSheetOpen(false);
      setSelectedPriceList(priceList);
      setIsEditSheetOpen(true);
    } else {
      toast.error(createError ?? "Failed to create price list");
    }
  };

  if (listError) {
    return (
      <div className="p-8 text-red-600">
        Error loading price lists: {listError}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={entitiesArray}
        onClickRow={handleEdit}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search price lists..."
        className="h-full"
        isLoading={listLoading && entitiesArray.length === 0}
        onCreate={canEditPriceLists ? handleOpenCreate : undefined}
        createButtonText="New"
        toolbarActions={
          <div className="flex items-center gap-2">
            {canEditPriceLists && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportSheetOpen(true)}
              >
                <Upload className="h-4 w-4 mr-1" />
                Import
              </Button>
            )}
            <Switch
              id="show-archived-price-lists"
              checked={showArchived}
              onCheckedChange={handleToggleArchived}
            />
            <Label
              htmlFor="show-archived-price-lists"
              className="text-sm whitespace-nowrap"
            >
              Show Archived
            </Label>
          </div>
        }
      />

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.PRICE_LIST_LINES}
        open={isImportSheetOpen}
        onOpenChange={setIsImportSheetOpen}
        onImportComplete={async () => {
          const ids = await fetchPriceLists();
          setDisplayedEntityIds(ids);
        }}
      />

      <Sheet open={isCreateSheetOpen} onOpenChange={setIsCreateSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create New Price List</SheetTitle>
            <SheetDescription>
              Define a new price list. You can add lines after creating it.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label htmlFor="create-price-list-name">Name</Label>
              <Input
                id="create-price-list-name"
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
                placeholder="Tier 1"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-price-list-description">Description</Label>
              <Input
                id="create-price-list-description"
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
                placeholder="Optional description"
              />
            </div>
          </div>

          <SheetFooter>
            <Button
              variant="outline"
              disabled={isCreating}
              onClick={() => setIsCreateSheetOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveCreate} disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Price List
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <PriceListEditorSheet
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        priceList={selectedPriceList}
        canEdit={
          canEditPriceLists && !selectedPriceList?.externalMappings.length
        }
        onSaved={() => {
          fetchPriceLists({ includeArchived: showArchived });
        }}
        onArchived={handleArchived}
      />

      <AlertDialog
        open={isArchiveDialogOpen}
        onOpenChange={setIsArchiveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Price List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive "
              {priceLists.get(pendingArchiveId ?? "")?.name}
              "? You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
